import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DOMPurify from "dompurify";

const toEmbedUrl = (url) => {
    if (!url) return null;
    try {
        const parsed = new URL(url);
        if (parsed.hostname.includes("youtu.be")) {
            const id = parsed.pathname.replace("/", "");
            return id ? `https://www.youtube.com/embed/${id}` : null;
        }
        if (parsed.hostname.includes("youtube.com")) {
            const id = parsed.searchParams.get("v");
            return id ? `https://www.youtube.com/embed/${id}` : null;
        }
        return null;
    } catch {
        return null;
    }
};

const normalizeMarkdownInput = (value) => String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/^\s*---\s+(.+)$/gm, "- $1")
    .replace(/^\s*\$\$\s+(.+)$/gm, "- $1");

const decodeHtmlEntities = (value) => String(value || "")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#x2F;/gi, "/");

const escapeHtml = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const inlineMarkdownToHtml = (value) => String(value || "")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/_(.+?)_/g, "<em>$1</em>")
    .replace(/~~(.+?)~~/g, "<s>$1</s>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');

const parseTwoColumnRow = (line) => {
    if (!line || (!line.includes("//") && !line.includes("|"))) return null;

    let normalizedLine = line.trim();
    if (
        (normalizedLine.startsWith("**") && normalizedLine.endsWith("**")) ||
        (normalizedLine.startsWith("__") && normalizedLine.endsWith("__"))
    ) {
        normalizedLine = normalizedLine.slice(2, -2).trim();
    }

    let delimiter = "//";
    let splitIndex = normalizedLine.indexOf("//");
    if (splitIndex === -1) {
        delimiter = "|";
        splitIndex = normalizedLine.indexOf("|");
        if (/^\|?[\s:\-]+(\|[\s:\-]+)+\|?$/.test(normalizedLine)) return null;
    }
    if (splitIndex <= 0 || splitIndex >= normalizedLine.length - delimiter.length) return null;

    const left = normalizedLine.slice(0, splitIndex).trim();
    const right = normalizedLine.slice(splitIndex + delimiter.length).trim();
    if (!left || !right) return null;

    return {
        left: inlineMarkdownToHtml(escapeHtml(left)),
        right: inlineMarkdownToHtml(escapeHtml(right))
    };
};

const markdownToHtml = (value) => {
    const normalized = normalizeMarkdownInput(value);
    if (!normalized.trim()) return "";

    const lines = normalized.split("\n");
    const html = [];
    let listType = null;
    let tableRows = [];
    let blankLineCount = 0;

    const flushList = () => {
        if (!listType) return;
        html.push(`</${listType}>`);
        listType = null;
    };
    const flushTable = () => {
        if (!tableRows.length) return;
        html.push("<table><tbody>");
        tableRows.forEach((row) => html.push(`<tr><td>${row.left}</td><td>${row.right}</td></tr>`));
        html.push("</tbody></table>");
        tableRows = [];
    };

    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) {
            flushList();
            flushTable();
            blankLineCount += 1;
            if (blankLineCount <= 2) {
                html.push("<p><br></p>");
            }
            continue;
        }
        blankLineCount = 0;

        const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
            flushList();
            flushTable();
            const level = headingMatch[1].length;
            html.push(`<h${level}>${inlineMarkdownToHtml(escapeHtml(decodeHtmlEntities(headingMatch[2])))}</h${level}>`);
            continue;
        }

        if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
            flushList();
            flushTable();
            html.push("<hr>");
            continue;
        }

        const unorderedMatch = line.match(/^[-*+]\s+(.+)$/);
        if (unorderedMatch) {
            flushTable();
            if (listType !== "ul") {
                flushList();
                listType = "ul";
                html.push("<ul>");
            }
            html.push(`<li>${inlineMarkdownToHtml(escapeHtml(decodeHtmlEntities(unorderedMatch[1])))}</li>`);
            continue;
        }

        const orderedMatch = line.match(/^\d+[.)]\s+(.+)$/);
        if (orderedMatch) {
            flushTable();
            if (listType !== "ol") {
                flushList();
                listType = "ol";
                html.push("<ol>");
            }
            html.push(`<li>${inlineMarkdownToHtml(escapeHtml(decodeHtmlEntities(orderedMatch[1])))}</li>`);
            continue;
        }

        const row = parseTwoColumnRow(line);
        if (row) {
            flushList();
            tableRows.push(row);
            continue;
        }

        flushList();
        flushTable();
        html.push(`<p>${inlineMarkdownToHtml(escapeHtml(decodeHtmlEntities(line)))}</p>`);
    }

    flushList();
    flushTable();
    return html.join("");
};

const toSanitizedRichHtml = (content) => {
    if (!content) return "";
    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(content);
    const normalized = hasHtml ? content : markdownToHtml(content);
    return DOMPurify.sanitize(normalized, {
        ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "span", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "a", "code", "s", "table", "tbody", "tr", "td"],
        ALLOWED_ATTR: ["style", "href", "target", "rel"],
        ALLOWED_STYLES: {
            "*": {
                color: [
                    /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
                    /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/
                ]
            }
        }
    });
};

const richContentSx = {
    color: "text.primary",
    fontSize: "0.875rem",
    lineHeight: 1.65,
    "& p": { margin: 0 },
    "& p + p": { mt: 0.5 },
    "& ul, & ol": { pl: 2, my: 0.5 },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
        mt: 0.4,
        mb: 0.6,
        fontSize: "0.9rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em"
    },
    "& code": { bgcolor: "#eeeeee", px: 0.5, borderRadius: 0.5, fontFamily: "monospace" },
    "& table": { width: "100%", borderCollapse: "collapse", mt: 0.5, mb: 0.5 },
    "& td": { p: "6px 10px", border: "none", verticalAlign: "top" },
    "& tr:nth-of-type(odd)": { bgcolor: "rgba(0, 0, 0, 0.06)" },
    "& tr:nth-of-type(even)": { bgcolor: "transparent" },
    "& tr:first-of-type td": { fontWeight: 700 }
};

const TrackInfoCard = ({ title, text }) => {
    const normalizedHtml = toSanitizedRichHtml((text || "").trim());
    return (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#fafafa", border: "1px solid", borderColor: "#e0e0e0", height: "100%" }}>
            <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: "#424242", letterSpacing: "0.08em", textTransform: "uppercase", mb: 1.2, fontSize: "0.8rem" }}
            >
                {title}
            </Typography>
            {normalizedHtml ? (
                <Box dangerouslySetInnerHTML={{ __html: normalizedHtml }} sx={richContentSx} />
            ) : (
                <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.65 }}>No info</Typography>
            )}
        </Box>
    );
};

const CoachSectionCard = ({ title, time, content }) => {
    const normalizedHtml = toSanitizedRichHtml(content);
    if (!normalizedHtml) return null;
    return (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#fafafa", border: "1px solid", borderColor: "#e0e0e0", position: "relative" }}>
            {time ? (
                <Chip
                    size="small"
                    icon={<AccessTimeIcon sx={{ fontSize: "0.95rem !important" }} />}
                    label={time}
                    sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        height: 24,
                        fontWeight: 600,
                        bgcolor: "rgba(25,118,210,0.10)",
                        color: "primary.main",
                        "& .MuiChip-label": { px: 1 }
                    }}
                />
            ) : null}
            <Box display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={1} mb={1.5} pr={time ? 14 : 0}>
                <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: "#424242", letterSpacing: "0.08em", textTransform: "uppercase", fontSize: "0.8rem" }}
                >
                    {title}
                </Typography>
            </Box>
            <Box dangerouslySetInnerHTML={{ __html: normalizedHtml }} sx={richContentSx} />
        </Box>
    );
};

export default function CoachInfoModal({ open, onClose, day, userRole, onEdit, isMobile = false }) {
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isMobileView = isMobile || isMobileScreen;
    const coach = day?.coachInfo || null;
    const links = coach?.videoLinks || [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobileView}
            maxWidth={isMobileView ? "sm" : "md"}
            fullWidth
            sx={{ zIndex: (theme) => theme.zIndex.modal + 200 }}
            PaperProps={{
                sx: {
                    maxHeight: "90vh",
                    m: isMobileView ? 0 : 2,
                    maxWidth: isMobileView ? "100%" : 960
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
                Coach Info - {day?.dayName || ""}
                {coach?.timeMinutes ? ` (${coach.timeMinutes} min)` : ""}
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: "#f5f5f5", px: { xs: 2, sm: 3 }, py: 2 }}>
                <Box sx={{ maxWidth: 900, mx: "auto" }}>
                    <Stack spacing={3.5}>
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ color: "#424242", letterSpacing: "0.1em", textTransform: "uppercase", mb: 2, fontSize: "0.95rem" }}
                            >
                                Class Tracks
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}><TrackInfoCard title="RX" text={day?.tracks?.rx} /></Grid>
                                <Grid item xs={12} md={4}><TrackInfoCard title="Intermediate" text={day?.tracks?.intermediate} /></Grid>
                                <Grid item xs={12} md={4}><TrackInfoCard title="Beginner" text={day?.tracks?.beginner} /></Grid>
                            </Grid>
                        </Box>

                        {!coach ? (
                            <Typography variant="body2" color="text.secondary">No coach info uploaded for this day.</Typography>
                        ) : (
                            <>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        sx={{ color: "#424242", letterSpacing: "0.1em", textTransform: "uppercase", mb: 2, fontSize: "0.95rem" }}
                                    >
                                        About the Workout
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={7}>
                                            <Stack spacing={2}>
                                                <CoachSectionCard title="Intended Stimulus" content={coach?.goalsIntendedStimulus} />
                                                <CoachSectionCard title="Coaching Goals" content={coach?.goalsCoaching} />
                                            </Stack>
                                        </Grid>
                                        {links.length > 0 ? (
                                            <Grid item xs={12} md={5}>
                                                <Box sx={{ maxWidth: 420 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        fontWeight={700}
                                                        sx={{ color: "#424242", letterSpacing: "0.08em", textTransform: "uppercase", mb: 1.5, fontSize: "0.8rem" }}
                                                    >
                                                        Video Overview
                                                    </Typography>
                                                    {(() => {
                                                        const link = links[0];
                                                        const embed = toEmbedUrl(link?.url);
                                                        return (
                                                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fafafa", border: "1px solid", borderColor: "#e0e0e0" }}>
                                                                <Typography variant="body2" sx={{ mb: 1, fontSize: "0.8rem" }}>
                                                                    <a href={link?.url} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                                                                        {link?.label || link?.url}
                                                                    </a>
                                                                </Typography>
                                                                {embed ? (
                                                                    <Box
                                                                        component="iframe"
                                                                        src={embed}
                                                                        title="video-overview"
                                                                        sx={{ width: "100%", maxWidth: 400, aspectRatio: "16/9", border: 0, borderRadius: 1 }}
                                                                        allowFullScreen
                                                                    />
                                                                ) : null}
                                                            </Box>
                                                        );
                                                    })()}
                                                </Box>
                                            </Grid>
                                        ) : null}
                                    </Grid>
                                </Box>

                                {links.length > 1 ? (
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            fontWeight={700}
                                            sx={{ color: "#424242", letterSpacing: "0.1em", textTransform: "uppercase", mb: 2, fontSize: "0.95rem" }}
                                        >
                                            Video Resources
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {links.slice(1).map((link, idx) => {
                                                const embed = toEmbedUrl(link.url);
                                                return (
                                                    <Grid item xs={12} sm={6} key={`${link.url}-${idx}`}>
                                                        <Box sx={{ maxWidth: 420, p: 1.5, borderRadius: 2, bgcolor: "#fafafa", border: "1px solid", borderColor: "#e0e0e0" }}>
                                                            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.8rem" }}>
                                                                <a href={link.url} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                                                                    {link.label || link.url}
                                                                </a>
                                                            </Typography>
                                                            {embed ? (
                                                                <Box
                                                                    component="iframe"
                                                                    src={embed}
                                                                    title={`video-${idx}`}
                                                                    sx={{ width: "100%", maxWidth: 400, aspectRatio: "16/9", border: 0, borderRadius: 1 }}
                                                                    allowFullScreen
                                                                />
                                                            ) : null}
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                ) : null}

                                <Box>
                                    <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        sx={{ color: "#424242", letterSpacing: "0.1em", textTransform: "uppercase", mb: 2, fontSize: "0.95rem" }}
                                    >
                                        Class Plan
                                    </Typography>
                                    <Stack spacing={2}>
                                        <CoachSectionCard title="Whiteboard Brief" time={coach?.whiteboardBriefTime} content={coach?.whiteboardBrief} />
                                        <CoachSectionCard title="General Warm-up (Glance)" content={coach?.generalWarmupGlance} />
                                        <CoachSectionCard title="General Warm-up" time={coach?.generalWarmupTime} content={coach?.generalWarmup} />
                                        <CoachSectionCard title="Specific Warm-up (Glance)" content={coach?.specificWarmupGlance} />
                                        <CoachSectionCard title="Specific Warm-up" time={coach?.specificWarmupTime} content={coach?.specificWarmup} />
                                        <CoachSectionCard title="Workout (Glance)" content={coach?.workoutGlance} />
                                        <CoachSectionCard title="Workout" time={coach?.workoutTime} content={coach?.workout} />
                                        <CoachSectionCard title="Cooldown (Glance)" content={coach?.cooldownGlance} />
                                        <CoachSectionCard title="Cooldown" time={coach?.cooldownTime} content={coach?.cooldown} />
                                    </Stack>
                                </Box>
                            </>
                        )}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {userRole === "affiliate" && typeof onEdit === "function" ? (
                    <Button onClick={onEdit} variant="outlined" color="primary">Edit</Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}
